/**
 * Skills Service
 * 
 * Manages agent skills: search, install, list local, sync to IDEs.
 * Supports two sources: Jai1 Server (internal) and npm `skills` package (open ecosystem).
 */
import { promises as fs } from 'fs';
import { join, basename } from 'path';
import { homedir } from 'os';
import { createHash } from 'crypto';
import { gunzipSync } from 'zlib';
import { execFile } from 'child_process';
import { promisify } from 'util';
import type { Jai1Config } from '../types/framework.types.js';
import { ComponentsService, type Component } from './components.service.js';

const execFileAsync = promisify(execFile);

/**
 * Local skill metadata parsed from SKILL.md frontmatter
 */
export interface LocalSkill {
    name: string;
    slug: string;
    description: string;
    path: string;
    fileCount: number;
    source?: SkillSource | 'unknown';
    sourceRef?: string;
    contentHash?: string;
    version?: string;
}

/**
 * Skill search result combining server and npm sources
 */
export interface SkillSearchResult {
    name: string;
    description: string;
    source: 'server' | 'npm';
    version?: string;
    downloads?: number;
}

export type SkillSource = 'jai1' | 'skills.sh';

export interface NormalizedSkillCandidate {
    id: string;
    source: SkillSource;
    sourceRef: string;
    name: string;
    description: string;
    popularity?: number;
    version?: string;
    tags?: string[];
}

export interface SkillCandidateDetail {
    schemaVersion: 1;
    candidate: NormalizedSkillCandidate & { contentHash: string };
    skillMd: string;
    files: Array<{
        path: string;
        size: number;
        hash: string;
        kind: 'instruction' | 'executable' | 'resource';
    }>;
    reviewFiles?: Array<{ path: string; contents: string }>;
    risk: {
        status: 'clear' | 'review' | 'high';
        complete: boolean;
        findings: string[];
        omittedFiles: string[];
    };
    cache: { hit: boolean; stale: boolean; fetchedAt: string };
}

interface SkillsServiceOptions {
    cacheDir?: string;
    fetcher?: typeof fetch;
    cacheTtlMs?: number;
}

/**
 * IDE skill sync target
 */
interface IDESkillTarget {
    id: string;
    name: string;
    icon: string;
    skillsPath: string;
}

/**
 * IDE configurations for skill syncing
 */
const IDE_SKILL_TARGETS: IDESkillTarget[] = [
    { id: 'cursor', name: 'Cursor', icon: '🔮', skillsPath: '.agents/skills' },
    { id: 'windsurf', name: 'Windsurf', icon: '🏄', skillsPath: '.windsurf/skills' },
    { id: 'antigravity', name: 'Antigravity', icon: '🚀', skillsPath: '.agent/skills' },
    { id: 'claude', name: 'Claude Code', icon: '🤖', skillsPath: '.claude/skills' },
    { id: 'opencode', name: 'OpenCode', icon: '💻', skillsPath: '.agents/skills' },
    { id: 'codex', name: 'Codex', icon: '🧠', skillsPath: '.agents/skills' },
];

export class SkillsService {
    private readonly componentsService: ComponentsService;
    private readonly cacheDir: string;
    private readonly fetcher: typeof fetch;
    private readonly cacheTtlMs: number;

    constructor(projectRoot: string = process.cwd(), options: SkillsServiceOptions = {}) {
        this.componentsService = new ComponentsService(projectRoot);
        this.cacheDir = options.cacheDir
            ?? process.env.JAI1_SKILLS_CACHE_DIR
            ?? join(homedir(), '.jai1', 'cache', 'skills');
        this.fetcher = options.fetcher ?? fetch;
        this.cacheTtlMs = options.cacheTtlMs ?? 24 * 60 * 60 * 1000;
    }

    async searchSkillsSh(
        query: string,
        options: { limit?: number; refresh?: boolean } = {},
    ): Promise<NormalizedSkillCandidate[]> {
        const limit = Math.max(1, Math.min(options.limit ?? 10, 10));
        const cachePath = this.getCachePath('search', query, String(limit));
        if (!options.refresh) {
            try {
                const cached = JSON.parse(await fs.readFile(cachePath, 'utf-8')) as {
                    fetchedAt: string;
                    candidates: NormalizedSkillCandidate[];
                };
                if (Date.now() - Date.parse(cached.fetchedAt) <= this.cacheTtlMs) {
                    return cached.candidates;
                }
            } catch {
                // Cache miss or malformed cache; fetch a fresh result.
            }
        }
        const params = new URLSearchParams({ q: query, limit: String(limit) });
        const response = await this.fetcher(`https://skills.sh/api/search?${params.toString()}`);
        if (!response.ok) throw new Error(`skills.sh search failed: HTTP ${response.status}`);
        const data = await response.json() as {
            skills?: Array<{ id: string; name: string; source?: string; installs?: number }>;
        };

        const candidates = (data.skills ?? []).slice(0, limit).map((skill) => {
            const sourceRef = `${skill.source || skill.id.split('/').slice(0, 2).join('/')}@${skill.name}`;
            return {
                id: `skills.sh:${sourceRef}`,
                source: 'skills.sh' as const,
                sourceRef,
                name: skill.name,
                description: '',
                popularity: skill.installs ?? 0,
            };
        });
        await fs.mkdir(join(cachePath, '..'), { recursive: true });
        await fs.writeFile(cachePath, JSON.stringify({
            fetchedAt: new Date().toISOString(),
            candidates,
        }), 'utf-8');
        return candidates;
    }

    async inspectCandidate(
        candidateId: string,
        options: { deep?: boolean; refresh?: boolean; config?: Jai1Config } = {},
    ): Promise<SkillCandidateDetail> {
        if (candidateId.startsWith('jai1:')) {
            return this.inspectJai1Candidate(candidateId, options);
        }
        if (!candidateId.startsWith('skills.sh:')) {
            throw new Error(`Unsupported candidate ID: ${candidateId}`);
        }

        const cachePath = this.getCachePath('detail', candidateId, options.deep ? 'deep' : 'summary');
        let staleCache: SkillCandidateDetail | null = null;
        if (!options.refresh) {
            staleCache = await this.readDetailCache(cachePath);
            if (staleCache && Date.now() - Date.parse(staleCache.cache.fetchedAt) <= this.cacheTtlMs) {
                return { ...staleCache, cache: { ...staleCache.cache, hit: true, stale: false } };
            }
        }

        const sourceRef = candidateId.slice('skills.sh:'.length);
        const separator = sourceRef.lastIndexOf('@');
        if (separator <= 0) throw new Error(`Invalid skills.sh candidate ID: ${candidateId}`);
        const repo = sourceRef.slice(0, separator);
        const skillSlug = sourceRef.slice(separator + 1);
        const [owner, repoName] = repo.split('/');
        if (!owner || !repoName || !skillSlug) throw new Error(`Invalid skills.sh candidate ID: ${candidateId}`);

        let response: Response;
        try {
            response = await this.fetcher(
                `https://skills.sh/api/download/${encodeURIComponent(owner)}/${encodeURIComponent(repoName)}/${encodeURIComponent(skillSlug)}`,
            );
        } catch (error) {
            if (staleCache) {
                return { ...staleCache, cache: { ...staleCache.cache, hit: true, stale: true } };
            }
            throw error;
        }
        if (!response.ok) throw new Error(`skills.sh inspect failed: HTTP ${response.status}`);
        const payload = await response.json() as {
            files?: Array<{ path: string; contents: string }>;
            hash?: string;
        };
        const sourceFiles = payload.files ?? [];
        const skillMd = sourceFiles.find((file) => basename(file.path).toUpperCase() === 'SKILL.MD')?.contents ?? '';
        if (!skillMd) throw new Error(`Candidate ${candidateId} has no SKILL.md`);
        const metadata = this.parseFrontmatter(skillMd);
        const omittedFiles: string[] = [];
        const reviewFiles: Array<{ path: string; contents: string }> = [];
        let reviewBytes = 0;
        const maxReviewBytes = 512 * 1024;
        const executablePattern = /(^|\/)(scripts?|bin)\/|\.(?:[cm]?js|ts|sh|bash|zsh|py|rb|ps1|ya?ml|json|toml)$/i;

        if (options.deep) {
            for (const file of sourceFiles) {
                if (!executablePattern.test(file.path) || basename(file.path).toUpperCase() === 'SKILL.MD') continue;
                const size = Buffer.byteLength(file.contents);
                if (reviewBytes + size > maxReviewBytes) {
                    omittedFiles.push(file.path);
                    continue;
                }
                reviewFiles.push(file);
                reviewBytes += size;
            }
        }

        const findings = options.deep ? this.findRiskSignals(reviewFiles) : [];
        const fetchedAt = new Date().toISOString();
        const detail: SkillCandidateDetail = {
            schemaVersion: 1,
            candidate: {
                id: candidateId,
                source: 'skills.sh',
                sourceRef,
                name: metadata.name || skillSlug,
                description: metadata.description,
                contentHash: payload.hash || this.hashContent(sourceFiles.map((file) => `${file.path}\0${file.contents}`).join('\0')),
            },
            skillMd,
            files: sourceFiles.map((file) => ({
                path: file.path,
                size: Buffer.byteLength(file.contents),
                hash: this.hashContent(file.contents),
                kind: basename(file.path).toUpperCase() === 'SKILL.MD'
                    ? 'instruction'
                    : executablePattern.test(file.path) ? 'executable' : 'resource',
            })),
            ...(options.deep ? { reviewFiles } : {}),
            risk: {
                status: findings.length > 0 ? 'review' : 'clear',
                complete: options.deep === true && omittedFiles.length === 0,
                findings,
                omittedFiles,
            },
            cache: { hit: false, stale: false, fetchedAt },
        };
        await fs.mkdir(join(cachePath, '..'), { recursive: true });
        await fs.writeFile(cachePath, JSON.stringify(detail), 'utf-8');
        return detail;
    }

    private async inspectJai1Candidate(
        candidateId: string,
        options: { deep?: boolean; refresh?: boolean; config?: Jai1Config },
    ): Promise<SkillCandidateDetail> {
        if (!options.config) throw new Error('Jai1 candidate inspection requires authentication config');
        const cachePath = this.getCachePath('detail', candidateId, options.deep ? 'deep' : 'summary');
        if (!options.refresh) {
            const cached = await this.readDetailCache(cachePath);
            if (cached && Date.now() - Date.parse(cached.cache.fetchedAt) <= this.cacheTtlMs) {
                return { ...cached, cache: { ...cached.cache, hit: true, stale: false } };
            }
        }

        const slug = candidateId.slice('jai1:'.length);
        const filepath = `skills/${slug}`;
        const response = await this.fetcher(
            `${options.config.apiUrl}/api/components/${encodeURIComponent(filepath)}?trackDownload=false`,
            { headers: { 'JAI1-Access-Key': options.config.accessKey } },
        );
        if (!response.ok) throw new Error(`Jai1 inspect failed: HTTP ${response.status}`);
        const component = await response.json() as Component;
        if (!component.content) throw new Error(`Candidate ${candidateId} has no content`);

        let sourceFiles: Array<{ path: string; contents: string }>;
        if (component.contentType === 'bundle' || component.contentType === 'zip') {
            const bundleJson = component.contentType === 'bundle'
                ? gunzipSync(Buffer.from(component.content, 'base64')).toString('utf-8')
                : component.content;
            const bundle = JSON.parse(bundleJson) as { main: string; assets?: Record<string, string> };
            sourceFiles = [
                { path: 'SKILL.md', contents: bundle.main },
                ...Object.entries(bundle.assets ?? {})
                    .filter(([path]) => path !== 'SKILL.md')
                    .map(([path, contents]) => ({ path, contents })),
            ];
        } else {
            sourceFiles = [{ path: 'SKILL.md', contents: component.content }];
        }

        const skillMd = sourceFiles.find((file) => basename(file.path).toUpperCase() === 'SKILL.MD')?.contents ?? '';
        const metadata = this.parseFrontmatter(skillMd);
        const executablePattern = /(^|\/)(scripts?|bin)\/|\.(?:[cm]?js|ts|sh|bash|zsh|py|rb|ps1|ya?ml|json|toml)$/i;
        const reviewFiles = options.deep
            ? sourceFiles.filter((file) => executablePattern.test(file.path) && basename(file.path).toUpperCase() !== 'SKILL.MD')
            : [];
        const findings = options.deep ? this.findRiskSignals(reviewFiles) : [];
        const fetchedAt = new Date().toISOString();
        const detail: SkillCandidateDetail = {
            schemaVersion: 1,
            candidate: {
                id: candidateId,
                source: 'jai1',
                sourceRef: slug,
                name: metadata.name || component.name || slug,
                description: metadata.description || component.description || '',
                popularity: component.downloads,
                version: component.version,
                tags: component.tags ?? [],
                contentHash: this.hashContent(sourceFiles.map((file) => `${file.path}\0${file.contents}`).join('\0')),
            },
            skillMd,
            files: sourceFiles.map((file) => ({
                path: file.path,
                size: Buffer.byteLength(file.contents),
                hash: this.hashContent(file.contents),
                kind: basename(file.path).toUpperCase() === 'SKILL.MD'
                    ? 'instruction'
                    : executablePattern.test(file.path) ? 'executable' : 'resource',
            })),
            ...(options.deep ? { reviewFiles } : {}),
            risk: {
                status: findings.length > 0 ? 'review' : 'clear',
                complete: options.deep === true,
                findings,
                omittedFiles: [],
            },
            cache: { hit: false, stale: false, fetchedAt },
        };
        await fs.mkdir(join(cachePath, '..'), { recursive: true });
        await fs.writeFile(cachePath, JSON.stringify(detail), 'utf-8');
        return detail;
    }

    /**
     * Search skills on Jai1 server
     */
    async searchFromServer(config: Jai1Config, query?: string): Promise<Component[]> {
        const options: { tag?: string; search?: string } = {};
        if (query) options.search = query;

        const components = await this.componentsService.list(config, options);

        // Filter to skills only (filepath starts with "skills/")
        return components.filter(c => c.filepath.startsWith('skills/'));
    }

    /**
     * Install skill from Jai1 server to .jai1/skills/
     */
    async installFromServer(config: Jai1Config, skillName: string, targetDir: string): Promise<void> {
        const filepath = skillName.startsWith('skills/') ? skillName : `skills/${skillName}`;
        await this.componentsService.install(config, filepath, targetDir);
    }

    /**
     * List locally installed skills from .jai1/skills/
     */
    async listLocal(projectRoot: string): Promise<LocalSkill[]> {
        const skillsDir = join(projectRoot, '.jai1', 'skills');
        const skills: LocalSkill[] = [];
        const manifest = await this.componentsService.getInstalled();

        try {
            const entries = await fs.readdir(skillsDir, { withFileTypes: true });

            for (const entry of entries) {
                if (!entry.isDirectory()) continue;

                const skillPath = join(skillsDir, entry.name);
                const skillMd = join(skillPath, 'SKILL.md');

                try {
                    await fs.access(skillMd);
                } catch {
                    continue; // No SKILL.md, skip
                }

                // Parse frontmatter from SKILL.md
                const content = await fs.readFile(skillMd, 'utf-8');
                const { name, description } = this.parseFrontmatter(content);

                // Count files in skill directory
                const fileCount = await this.countFiles(skillPath);
                const installed = manifest[`skills/${entry.name}`];

                skills.push({
                    name: name || entry.name,
                    slug: entry.name,
                    description: description || '',
                    path: skillPath,
                    fileCount,
                    source: installed?.source ?? (installed ? 'jai1' : 'unknown'),
                    sourceRef: installed?.sourceRef,
                    contentHash: installed?.contentHash ?? installed?.checksum,
                    version: installed?.version,
                });
            }
        } catch {
            // skills directory doesn't exist
        }

        return skills.sort((a, b) => a.slug.localeCompare(b.slug));
    }

    /**
     * Get detailed info for a single skill
     */
    async getSkillInfo(projectRoot: string, skillName: string): Promise<LocalSkill | null> {
        const skillPath = join(projectRoot, '.jai1', 'skills', skillName);
        const skillMd = join(skillPath, 'SKILL.md');

        try {
            await fs.access(skillMd);
        } catch {
            return null;
        }

        const content = await fs.readFile(skillMd, 'utf-8');
        const { name, description } = this.parseFrontmatter(content);
        const fileCount = await this.countFiles(skillPath);

        return {
            name: name || skillName,
            slug: skillName,
            description: description || '',
            path: skillPath,
            fileCount,
        };
    }

    /**
     * Search skills via npm `skills` package (wrapper)
     */
    async npmSkillsFind(query: string): Promise<string> {
        try {
            const { stdout } = await execFileAsync('npx', ['-y', 'skills', 'find', query], {
                timeout: 30_000,
            });
            return stdout;
        } catch (error) {
            throw new Error(
                `npm skills find failed: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    /**
     * Install skill via npm `skills` package (wrapper)
     *
     * Source format: "owner/repo" or "owner/repo@skill"
     * Runs: npx skills add owner/repo [--skill name] --copy --yes
     * Then copies installed skills into .jai1/skills/
     */
    async npmSkillsAdd(
        source: string,
        projectRoot: string,
    ): Promise<{ output: string; installedSlugs: string[] }> {
        // Parse "owner/repo@skill" format
        const { repo, skill } = this.parseSkillshSource(source);

        // Build command args: install only to universal agent (.agents/skills/)
        const args = ['-y', 'skills', 'add', repo, '--copy', '-a', 'universal', '-y'];
        if (skill) {
            args.push('--skill', skill);
        }

        try {
            const { stdout } = await execFileAsync('npx', args, {
                timeout: 60_000,
                cwd: projectRoot,
            });

            // After install, copy from agent dirs into .jai1/skills/
            const installedSlugs = await this.copySkillshResultsToJai1(projectRoot, skill, source);

            return { output: stdout, installedSlugs };
        } catch (error) {
            throw new Error(
                `skills.sh add failed: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    /**
     * Parse skills.sh source format: "owner/repo@skill" → { repo, skill }
     */
    private parseSkillshSource(source: string): { repo: string; skill?: string } {
        const atIndex = source.lastIndexOf('@');

        // Check if @ is part of owner/repo (no slash after @) or is skill separator
        if (atIndex > 0 && !source.substring(atIndex + 1).includes('/')) {
            return {
                repo: source.substring(0, atIndex),
                skill: source.substring(atIndex + 1),
            };
        }

        return { repo: source };
    }

    /**
     * After npx skills add, copy newly installed skills from .agents/skills/ into .jai1/skills/
     */
    private async copySkillshResultsToJai1(
        projectRoot: string,
        specificSkill?: string,
        sourceRef?: string,
    ): Promise<string[]> {
        const jai1SkillsDir = join(projectRoot, '.jai1', 'skills');
        await fs.mkdir(jai1SkillsDir, { recursive: true });
        const installedSlugs: string[] = [];

        // npx skills installs to .agents/skills/ when using -a universal
        const universalDir = join(projectRoot, '.agents', 'skills');

        try {
            const entries = await fs.readdir(universalDir, { withFileTypes: true });

            for (const entry of entries) {
                if (!entry.isDirectory()) continue;
                if (specificSkill && entry.name !== specificSkill) continue;

                const srcSkill = join(universalDir, entry.name);
                const skillMd = join(srcSkill, 'SKILL.md');

                // Only copy if it has SKILL.md
                try {
                    await fs.access(skillMd);
                } catch {
                    continue;
                }

                // Copy to .jai1/skills/
                const targetSkill = join(jai1SkillsDir, entry.name);
                try {
                    await fs.access(targetSkill);
                    // Already exists, skip
                } catch {
                    await fs.mkdir(targetSkill, { recursive: true });
                    await this.copyDir(srcSkill, targetSkill);
                    const skillContent = await fs.readFile(skillMd, 'utf-8');
                    const versionMatch = skillContent.match(/^\s*version:\s*["']?([^\n"']+)/m);
                    const contentHash = this.hashContent(skillContent);
                    await this.componentsService.markInstalled(
                        `skills/${entry.name}`,
                        versionMatch?.[1]?.trim() || '0.0.0',
                        contentHash.slice(0, 16),
                        {
                            source: 'skills.sh',
                            sourceRef,
                            contentHash,
                            publisher: sourceRef?.split('/')[0],
                        },
                    );
                    installedSlugs.push(entry.name);
                }
            }
        } catch {
            // .agents/skills/ doesn't exist, skip
        }
        return installedSlugs;
    }

    /**
     * Get available IDE skill targets
     */
    getIDETargets(): IDESkillTarget[] {
        return IDE_SKILL_TARGETS;
    }

    /**
     * Get IDE skill target by ID
     */
    getIDETarget(id: string): IDESkillTarget | undefined {
        return IDE_SKILL_TARGETS.find(t => t.id === id);
    }

    /**
     * Sync skills from .jai1/skills/ to IDE directories
     */
    async syncToIdes(
        projectRoot: string,
        ides: string[],
        skillSlugs?: string[],
        onProgress?: (result: { ide: string; skill: string; status: 'created' | 'updated' | 'error'; path: string; error?: string }) => void
    ): Promise<{ created: number; updated: number; errors: number }> {
        const localSkills = await this.listLocal(projectRoot);

        // Filter skills if specific ones requested
        const skillsToSync = skillSlugs
            ? localSkills.filter(s => skillSlugs.includes(s.slug))
            : localSkills;

        if (skillsToSync.length === 0) {
            return { created: 0, updated: 0, errors: 0 };
        }

        let created = 0;
        let updated = 0;
        let errors = 0;

        const syncedTargetPaths = new Set<string>();

        for (const ide of ides) {
            const target = this.getIDETarget(ide);
            if (!target) continue;

            for (const skill of skillsToSync) {
                const targetPath = join(projectRoot, target.skillsPath, skill.slug);
                if (syncedTargetPaths.has(targetPath)) {
                    continue;
                }
                syncedTargetPaths.add(targetPath);

                try {
                    // Check if already exists
                    let status: 'created' | 'updated' = 'created';
                    try {
                        await fs.access(targetPath);
                        status = 'updated';
                        // Remove old folder
                        await fs.rm(targetPath, { recursive: true, force: true });
                    } catch {
                        // Target doesn't exist, will create
                    }

                    // Copy skill folder
                    await fs.mkdir(targetPath, { recursive: true });
                    await this.copyDir(skill.path, targetPath);

                    if (status === 'created') created++;
                    else updated++;

                    onProgress?.({
                        ide: target.name,
                        skill: skill.slug,
                        status,
                        path: targetPath,
                    });
                } catch (error) {
                    errors++;
                    onProgress?.({
                        ide: target.name,
                        skill: skill.slug,
                        status: 'error',
                        path: targetPath,
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        }

        return { created, updated, errors };
    }

    /**
     * Parse YAML frontmatter from SKILL.md
     */
    private parseFrontmatter(content: string): { name: string; description: string } {
        const match = content.match(/^---\n([\s\S]*?)\n---/);
        if (!match) {
            // Try to extract name from first heading
            const headingMatch = content.match(/^#\s+(.+)/m);
            return {
                name: headingMatch?.[1] || '',
                description: '',
            };
        }

        const frontmatter = match[1] ?? '';
        let name = '';
        let description = '';

        for (const line of frontmatter.split('\n')) {
            const nameMatch = line.match(/^name:\s*(.+)/);
            if (nameMatch?.[1]) name = nameMatch[1].trim().replace(/^["']|["']$/g, '');

            const descMatch = line.match(/^description:\s*(.+)/);
            if (descMatch?.[1]) description = descMatch[1].trim().replace(/^["']|["']$/g, '');
        }

        return { name, description };
    }

    private getCachePath(kind: string, ...parts: string[]): string {
        const key = this.hashContent(parts.join('\0'));
        return join(this.cacheDir, kind, `${key}.json`);
    }

    private async readDetailCache(path: string): Promise<SkillCandidateDetail | null> {
        try {
            return JSON.parse(await fs.readFile(path, 'utf-8')) as SkillCandidateDetail;
        } catch {
            return null;
        }
    }

    private hashContent(content: string): string {
        return createHash('sha256').update(content).digest('hex');
    }

    private findRiskSignals(files: Array<{ path: string; contents: string }>): string[] {
        const findings = new Set<string>();
        const checks: Array<[RegExp, string]> = [
            [/\brm\s+-rf\b|\bRemove-Item\b[^\n]*-Recurse/i, 'destructive-file-operation'],
            [/\bcurl\b[^\n]*\|\s*(?:ba)?sh\b|\bwget\b[^\n]*\|\s*(?:ba)?sh\b/i, 'remote-code-execution'],
            [/\.env\b|process\.env|credentials?|access[_-]?key|secret/i, 'secret-access'],
            [/\bsudo\b|chmod\s+777/i, 'privilege-escalation'],
        ];
        for (const file of files) {
            for (const [pattern, code] of checks) {
                if (pattern.test(file.contents)) findings.add(`${code}:${file.path}`);
            }
        }
        return [...findings];
    }

    /**
     * Count files in a directory recursively
     */
    private async countFiles(dirPath: string): Promise<number> {
        let count = 0;
        const entries = await fs.readdir(dirPath, { withFileTypes: true });

        for (const entry of entries) {
            if (entry.isDirectory()) {
                count += await this.countFiles(join(dirPath, entry.name));
            } else {
                count++;
            }
        }

        return count;
    }

    /**
     * Copy directory recursively
     */
    private async copyDir(source: string, target: string): Promise<void> {
        const entries = await fs.readdir(source, { withFileTypes: true });

        for (const entry of entries) {
            const srcPath = join(source, entry.name);
            const tgtPath = join(target, entry.name);

            if (entry.isDirectory()) {
                await fs.mkdir(tgtPath, { recursive: true });
                await this.copyDir(srcPath, tgtPath);
            } else {
                await fs.copyFile(srcPath, tgtPath);
            }
        }
    }
}
